import React from 'react';
import {
  BookOpen, Gauge, ClipboardCheck, Wind, Pill, Activity, Syringe, ScanHeart, HeartPulse,
  Waves, Brain, Crosshair, Flame, Baby, Ambulance, Droplets, HeartHandshake,
  Building2, Scissors, Siren, ShieldCheck, Users, FlaskConical,
} from 'lucide-react';
import { IconLungs, IconEmergencyBed } from '@tabler/icons-react';
import PregnantIcon from './PregnantIcon';
import { HEALTHICON_PATHS } from './healthiconPaths';

/**
 * One glyph per Education Center topic: Healthicons for every current topic id (see HealthIcon
 * below); the Lucide / Tabler map remains for older stored ids and as the fallback.
 * Keys are the `icon` field in data/educationTaxonomy.js (older aliases kept for stored data).
 */
const ICONS = {
  general: Gauge,
  fundamentals: Gauge,
  preop: ClipboardCheck,
  airway: Wind,
  pharmacology: Pill,
  physiology: Activity,
  procedures: Syringe,
  echo: ScanHeart,
  cardiac: HeartPulse,
  thoracic: IconLungs,
  vascular: Waves,
  neuro: Brain,
  regional: Crosshair,
  pain: Flame,
  ob: PregnantIcon,
  obstetric: PregnantIcon,
  pediatric: Baby,
  icu: IconEmergencyBed,
  trauma: Ambulance,
  blood: Droplets,
  'blood-transfusion': Droplets,
  transplant: HeartHandshake,
  nora: Building2,
  subspecialty: Scissors,
  crisis: Siren,
  safety: ShieldCheck,
  practice: ShieldCheck,
  professional: Users,
  research: FlaskConical,
};

const TABLER = new Set([IconLungs, IconEmergencyBed]);

/*
 * Topic tiles use Healthicons (healthicons.org, MIT): the clinical vocabulary Lucide lacks.
 * The rule across the app: any library inside a coloured tile, Lucide wherever a glyph sits
 * beside text. Healthicons leave ~5 units of air around the glyph, so the viewBox crops it and
 * the glyph fills the tile the way Lucide's do.
 */
const HealthIcon = ({ type, className }) => {
  const inner = HEALTHICON_PATHS[type];
  if (!inner) return null;
  return (
    <svg
      viewBox="5 5 38 38"
      className={className}
      fill="currentColor"
      aria-hidden="true"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
};

const TopicIcon = ({ type, className = 'w-8 h-8' }) => {
  if (HEALTHICON_PATHS[type]) return <HealthIcon type={type} className={className} />;
  const Icon = ICONS[type] || BookOpen;
  // Tabler takes its stroke width as `stroke`; Lucide and our own glyphs as `strokeWidth`
  if (TABLER.has(Icon)) return <Icon className={className} stroke={1.75} />;
  return <Icon className={className} strokeWidth={1.75} />;
};

export default TopicIcon;
